import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../lib/seo';
import { ContactForm } from '../components/ContactForm';

export function Prices({ settings }) {
  const phone = settings?.phone || "82 82 10 10";

  return (
    <>
      <SEO 
        title="Pris & Abonnement | Fodtid"
        description="Få overblik over priser på professionel fodbehandling i eget hjem. Enkeltbehandling kun 499 kr. inkl. kørsel eller abonnement til 119 kr./mdr. Ring 82 82 10 10."
      />

      {/* Header */}
      <section className="bg-canvas py-14 sm:py-18 border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 text-center space-y-3">
          <span className="text-xs uppercase font-bold tracking-widest text-brand-charcoal bg-surface px-3.5 py-1 rounded-full border border-brand-border">
            Klare & Gennemskuelige Priser
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-brand-charcoal">
            Priser & Abonnement
          </h1>
          <p className="text-brand-muted text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            Professionel fodpleje af højeste kvalitet i dit eget hjem – altid med kørsel inkluderet på hele Sjælland (minus øerne).
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 sm:py-24 bg-canvas border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16">
            
            {/* Card 1: Enkeltbehandling */}
            <div className="bg-surface/60 rounded-3xl p-8 sm:p-10 border border-brand-border flex flex-col justify-between hover:shadow-md transition-all">
              <div className="space-y-6">
                <div className="space-y-2">
                  <span className="text-xs uppercase font-bold tracking-wider text-brand-muted">
                    Enkeltbesøg
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-charcoal">
                    Enkeltbehandling
                  </h2>
                  <p className="text-xs text-brand-muted">
                    Perfekt til dig, der ønsker en grundig behandling efter behov.
                  </p>
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-4xl sm:text-5xl font-bold text-brand-charcoal">499 kr.</span>
                  <span className="text-xs text-brand-muted font-semibold">/ pr. behandling</span>
                </div>

                <div className="pt-4 border-t border-brand-border space-y-3 text-xs text-brand-charcoal">
                  <div className="font-bold text-brand-charcoal uppercase tracking-wider text-[11px]">
                    Behandlingen inkluderer:
                  </div>
                  <ul className="space-y-2.5">
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Fodbad og grundig undersøgelse af fødderne</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Klipning, slibning og polering af negle</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Behandling af neglebånd og neglefals</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Fjernelse og beskæring af hård hud</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Behandling af ligtorne, hælrevner og negleproblemer</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Afsluttende let massage med plejende urtecreme</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Kørsel inkluderet til din adresse</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-8 mt-8 border-t border-brand-border">
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold py-3.5 px-6 rounded-full text-center shadow-md transition-all text-xs uppercase tracking-wider"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Bestil enkeltbehandling (499 kr.)</span>
                </Link>
              </div>
            </div>

            {/* Card 2: Abonnement */}
            <div className="bg-surface rounded-3xl p-8 sm:p-10 border-2 border-brand-charcoal shadow-md flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-brand-charcoal text-canvas text-[10px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-bl-2xl">
                Mest fordelagtig
              </div>

              <div className="space-y-6 relative z-10">
                <div className="space-y-2">
                  <span className="text-xs uppercase font-bold tracking-wider text-brand-muted">
                    Løbende Fodpleje
                  </span>
                  <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-charcoal">
                    Fodpleje Abonnement
                  </h2>
                  <p className="text-xs text-brand-muted">
                    Nemt, trygt og til at betale. 4 faste behandlinger om året (hver 3. måned).
                  </p>
                </div>

                <div className="flex items-baseline gap-2">
                  <span className="font-serif text-4xl sm:text-5xl font-bold text-brand-charcoal">119 kr.</span>
                  <span className="text-xs text-brand-muted font-semibold">/ pr. måned</span>
                </div>

                <div className="pt-4 border-t border-brand-border space-y-3 text-xs text-brand-charcoal">
                  <div className="font-bold text-brand-charcoal uppercase tracking-wider text-[11px]">
                    Abonnementsfordele:
                  </div>
                  <ul className="space-y-2.5">
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span><strong>4 komplette fodbehandlinger</strong> årligt i dit eget hjem</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Vi holder styr på dine intervaller og booker for dig</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Fast lav månedlig pris – ingen ubehagelige regninger</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Kørsel altid 100% inkluderet</span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Husk: Du kan fortsat søge <strong>helbredstillæg</strong></span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <i className="fa-solid fa-circle-check text-brand-accent mt-0.5" aria-hidden="true" />
                      <span>Ingen binding – opsig når som helst</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="pt-8 mt-8 border-t border-brand-border relative z-10 space-y-3">
                <Link
                  to="/kontakt"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#A67C3D] hover:bg-[#91692E] text-white font-bold py-3.5 px-6 rounded-full text-center shadow-lg transition-all text-xs uppercase tracking-wider"
                >
                  <i className="fa-solid fa-repeat" aria-hidden="true" />
                  <span>Tilmeld abonnement (119 kr./mdr.)</span>
                </Link>

                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-brand-muted">
                  <a
                    href="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/den-glade-fodmand-abonnement-119-kr.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:text-brand-charcoal underline"
                  >
                    <i className="fa-solid fa-file-pdf text-brand-accent" aria-hidden="true" />
                    <span>Abonnement priser (PDF)</span>
                  </a>
                  <span>•</span>
                  <a
                    href="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/abonnementsbetingelser.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:text-brand-charcoal underline"
                  >
                    <i className="fa-solid fa-file-pdf text-brand-accent" aria-hidden="true" />
                    <span>Abonnementsbetingelser (PDF)</span>
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Practical Info & Guidelines */}
          <div className="max-w-4xl mx-auto bg-surface/50 rounded-3xl p-8 sm:p-10 border border-brand-border space-y-8">
            <h3 className="font-serif text-2xl font-bold text-brand-charcoal text-center">
              Praktisk Information om Betaling & Tilskud
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs text-brand-muted">
              
              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center text-base">
                  <i className="fa-solid fa-credit-card text-brand-accent" aria-hidden="true" />
                </div>
                <h4 className="font-bold text-brand-charcoal text-sm">Betalingsformer</h4>
                <p className="leading-relaxed">
                  Du kan betale kontant, via MobilePay eller modtage en online faktura til betaling via netbank efter endt behandling.
                </p>
              </div>

              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center text-base">
                  <i className="fa-solid fa-hand-holding-dollar text-brand-accent" aria-hidden="true" />
                </div>
                <h4 className="font-bold text-brand-charcoal text-sm">Helbredstillæg</h4>
                <p className="leading-relaxed">
                  Folkepensionister og førtidspensionister med helbredstillæg kan få refunderet op til 85% af beløbet gennem kommunen. Vi udsteder specificeret kvittering.
                </p>
              </div>

              <div className="space-y-2">
                <div className="w-10 h-10 rounded-full bg-surface border border-brand-border text-brand-charcoal flex items-center justify-center text-base">
                  <i className="fa-solid fa-van-shuttle text-brand-accent" aria-hidden="true" />
                </div>
                <h4 className="font-bold text-brand-charcoal text-sm">Kørsel Inkluderet</h4>
                <p className="leading-relaxed">
                  Alle priser er inklusive kørsel og transport af udstyr på hele Sjælland (minus øerne).
                </p>
              </div>

            </div>

            {/* Price list & condition download links */}
            <div className="pt-6 border-t border-brand-border flex flex-wrap items-center justify-center gap-6 sm:gap-8 text-xs font-bold text-brand-charcoal">
              <a 
                href="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/den-glade-fodmand-logo.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-brand-accent transition-colors"
              >
                <i className="fa-solid fa-file-pdf text-brand-accent text-sm" aria-hidden="true" />
                <span>Hent samlet prisliste som PDF</span>
              </a>

              <a 
                href="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/den-glade-fodmand-abonnement-119-kr.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-brand-accent transition-colors"
              >
                <i className="fa-solid fa-file-pdf text-brand-accent text-sm" aria-hidden="true" />
                <span>Hent abonnement priser som PDF</span>
              </a>

              <a 
                href="https://assets.auxodev.dk/assets/d4428667-cde9-4c31-9224-88278506a01a/abonnementsbetingelser.pdf"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 hover:text-brand-accent transition-colors"
              >
                <i className="fa-solid fa-file-pdf text-brand-accent text-sm" aria-hidden="true" />
                <span>Hent abonnementsbetingelser som PDF</span>
              </a>
            </div>

          </div>

        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 sm:py-24 bg-surface/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <div className="bg-canvas rounded-3xl p-6 sm:p-10 border border-brand-border shadow-sm">
            <div className="text-center mb-8 space-y-2">
              <span className="text-xs uppercase font-bold tracking-widest text-brand-muted">
                Bestilling
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-brand-charcoal">
                Bestil din tid online
              </h2>
              <p className="text-brand-muted text-xs sm:text-sm">
                Vælg om du ønsker enkeltbehandling eller abonnement, så vender vi tilbage hurtigst muligt.
              </p>
            </div>
            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
}
